import { ContractLanguageSection } from "components/ContractLanguageSection";
import { IRuleAttachment } from "components/ContractLanguageSection/types";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import { InpatientRulesetType } from "types/InpatientRuleset";

import { InpatientCalculationExample } from "./InpatientCalculationExamples";

export interface IInpatientRulesetFormData {
  providerId: string | null;
  payerId: number | null;
  name: string;
  drgCodes: string[];
  type: InpatientRulesetType;
  isDefault?: boolean;
  // Fixed Cost fields
  costToChargeRatio: number | null;
  fixedLossThresholdInCents: number | null;
  outlierPercent: number | null;
  // Pass Through fields
  thresholdInCents: number | null;
  chargeMultiplier: number | null;
  // Excessive LOS fields
  formula: string;
  // Transfer fields
  losFormula: "LOS" | "LOS_PLUS_1" | "LOS_MINUS_1";
  dischargeStatusCodes: string[];
  // Stop Loss fields
  thresholdMultiplier: number | null;
  paymentPercent: number | null;
  // Charge Outlier fields
  chargeOutlierThresholdType: "FLAT" | "MULTIPLIER";
  chargeOutlierThresholdAmountInCents: number | null;
  chargeOutlierThresholdMultiplier: number | null;
  chargeOutlierPercent: number | null;
  chargeOutlierFloorInCents: number | null;
  // Inpatient Outlier Rules (CLE-2120) — flat state
  ipoTriggerType:
    | "STANDARD_CHARGE_THRESHOLD"
    | "MODIFIED_CHARGE_THRESHOLD"
    | "LOS_RANGE"
    | "PER_CODE_CHARGE_THRESHOLD"
    | "DISCHARGE_STATUS"
    | "ALWAYS";
  ipoCostToChargeRatio: number | null;
  ipoThresholdMethod: "FIXED" | "MULTIPLIER" | "BASE_PLUS_FIXED";
  ipoBaseReference: "BASE_PAYMENT" | "DRG_RATE_ONLY";
  ipoFixedAmountInCents: number | null;
  ipoMultiplier: number | null;
  ipoChargeFloorInCents: number | null;
  ipoLosOperator: "GT" | "GTE" | "EQ" | "LTE" | "LT";
  ipoLosDays: number | null;
  ipoLosDaysSource: "MANUAL" | "FEE_SCHEDULE";
  ipoCodeChargeFloorInCents: number | null;
  ipoOnlyCodesWithoutFeeSchedule: boolean;
  ipoDischargeStatusCodes: string[];
  ipoFormulaType:
    | "PER_DIEM_X_LOS"
    | "PER_DIEM_X_EXCESS_LOS"
    | "FLAT_RATE"
    | "PERCENT_OF_CHARGES"
    | "PERCENT_OF_EXCESS_CHARGES"
    | "CASE_RATE_PLUS_PER_DIEM";
  ipoPerDiemSource:
    | "MANUAL"
    | "CASE_RATE_DIV_ALOS_TIMES_MULTIPLIER"
    | "CASE_RATE_DIV_ACTUAL_LOS"
    | "CASE_RATE_DIV_ALOS";
  ipoManualPerDiemInCents: number | null;
  ipoLosAdjustment: "NONE" | "PLUS_1" | "MINUS_1";
  ipoCapAtDrgRate: boolean;
  ipoExcessLosBase: number | null;
  ipoExcessLosBaseSource: "MANUAL" | "FEE_SCHEDULE";
  ipoFlatRateInCents: number | null;
  ipoPercent: number | null;
  ipoCaseRateInCents: number | null;
  ipoIncludedDays: number | null;
  ipoCaseRatePerDiemInCents: number | null;
  ipoMode: "REPLACE" | "ADDITIVE" | "MAX";
  // Contract language (CLE-1935) — applies to every sub-type.
  contractLanguage: string;
}

// Type dropdown for NEW rules per CLE-2120 v3 spec.
// Existing rules of legacy types (Fixed Cost, Excessive LOS, Stop Loss, Charge Outlier)
// continue to load and render correctly via the existing conditional UI sections — they
// just can't be created from scratch anymore. Use Inpatient Outlier Rules for those patterns.
// Pass Through and Transfer remain as separate types because v3 doesn't have triggers
// that fit them (per-CPT-code iteration / discharge status).
const RULESET_TYPES = [
  { label: "Inpatient Outlier Rules", value: "INPATIENT_OUTLIER" as const },
  { label: "Pass Through", value: "PASS_THROUGH" as const },
  { label: "Transfer", value: "TRANSFER" as const },
];

// Trigger options per CLE-2120 v3 spec — only 3.
// PER_CODE_CHARGE_THRESHOLD, DISCHARGE_STATUS, ALWAYS are still in the engine code paths
// but not exposed here because v3 doesn't use them (Pass Through and Transfer remain
// separate top-level types).
const IPO_TRIGGER_TYPES = [
  {
    label: "Standard Charge Threshold",
    value: "STANDARD_CHARGE_THRESHOLD" as const,
  },
  {
    label: "Modified Charge Threshold (cost-to-charge)",
    value: "MODIFIED_CHARGE_THRESHOLD" as const,
  },
  { label: "LOS Range", value: "LOS_RANGE" as const },
];

const IPO_THRESHOLD_METHODS = [
  { label: "Fixed amount", value: "FIXED" as const },
  { label: "Multiplier of base", value: "MULTIPLIER" as const },
  { label: "Base + fixed amount", value: "BASE_PLUS_FIXED" as const },
];

const IPO_BASE_REFERENCES = [
  {
    label: "Base Payment (DRG + LOS + pass-through)",
    value: "BASE_PAYMENT" as const,
  },
  { label: "DRG Rate Only (raw 837 precalc)", value: "DRG_RATE_ONLY" as const },
];

const IPO_LOS_OPERATORS = [
  { label: "> (greater than)", value: "GT" as const },
  { label: ">= (at least)", value: "GTE" as const },
  { label: "= (equals)", value: "EQ" as const },
  { label: "<= (at most)", value: "LTE" as const },
  { label: "< (less than)", value: "LT" as const },
];

const IPO_DAYS_SOURCES = [
  { label: "Manual", value: "MANUAL" as const },
  {
    label: "From Fee Schedule (drgLosOutlierThreshold)",
    value: "FEE_SCHEDULE" as const,
  },
];

// Formula options per CLE-2120 v3 spec — only 5.
// CASE_RATE_PLUS_PER_DIEM is still in the engine code paths but not exposed here.
const IPO_FORMULA_TYPES = [
  { label: "Per Diem × LOS", value: "PER_DIEM_X_LOS" as const },
  {
    label: "Per Diem × Excess LOS",
    value: "PER_DIEM_X_EXCESS_LOS" as const,
  },
  { label: "Flat Rate", value: "FLAT_RATE" as const },
  { label: "Percent of Charges", value: "PERCENT_OF_CHARGES" as const },
  {
    label: "Percent of Excess Charges",
    value: "PERCENT_OF_EXCESS_CHARGES" as const,
  },
];

const IPO_PER_DIEM_SOURCES = [
  { label: "Manual", value: "MANUAL" as const },
  {
    label: "(DRG Rate ÷ ALOS) × Outlier Multiplier (fee schedule)",
    value: "CASE_RATE_DIV_ALOS_TIMES_MULTIPLIER" as const,
  },
  {
    label: "(DRG Rate ÷ Actual LOS)",
    value: "CASE_RATE_DIV_ACTUAL_LOS" as const,
  },
  {
    label: "(DRG Rate ÷ ALOS) (fee schedule)",
    value: "CASE_RATE_DIV_ALOS" as const,
  },
];

const IPO_MODES = [
  { label: "Replace base expected amount", value: "REPLACE" as const },
  { label: "Add to base expected amount", value: "ADDITIVE" as const },
  { label: "Max vs base expected amount", value: "MAX" as const },
];

const EXCESSIVE_LOS_FORMULAS = [
  { label: "BCBST Day Outlier", value: "BCBST Day Outlier" },
];

const CHARGE_OUTLIER_THRESHOLD_TYPES = [
  { label: "Flat Dollar Amount", value: "FLAT" as const },
  { label: "Multiplier of Base Expected", value: "MULTIPLIER" as const },
];

const TRANSFER_LOS_FORMULAS = [
  { label: "Per Diem × (LOS - 1)", value: "LOS_MINUS_1" as const },
  { label: "Per Diem × LOS", value: "LOS" as const },
  { label: "Per Diem × (LOS + 1)", value: "LOS_PLUS_1" as const },
];

const DEFAULT_TRANSFER_DISCHARGE_CODES = [
  "02",
  "05",
  "62",
  "63",
  "65",
  "66",
  "70",
  "71",
  "72",
  "73",
  "74",
  "75",
  "76",
  "77",
  "78",
  "79",
  "80",
  "81",
  "82",
  "95",
];

export const INITIAL_FORM_DATA: IInpatientRulesetFormData = {
  providerId: null,
  payerId: null,
  name: "",
  drgCodes: [],
  type: "FIXED_COST",
  isDefault: false,
  costToChargeRatio: null,
  fixedLossThresholdInCents: null,
  outlierPercent: 80,
  thresholdInCents: 0,
  chargeMultiplier: null,
  formula: "BCBST Day Outlier",
  losFormula: "LOS_PLUS_1",
  dischargeStatusCodes: [...DEFAULT_TRANSFER_DISCHARGE_CODES],
  thresholdMultiplier: null,
  paymentPercent: 75,
  chargeOutlierThresholdType: "FLAT",
  chargeOutlierThresholdAmountInCents: null,
  chargeOutlierThresholdMultiplier: null,
  chargeOutlierPercent: null,
  chargeOutlierFloorInCents: null,
  ipoTriggerType: "STANDARD_CHARGE_THRESHOLD",
  ipoCostToChargeRatio: null,
  ipoThresholdMethod: "FIXED",
  ipoBaseReference: "BASE_PAYMENT",
  ipoFixedAmountInCents: null,
  ipoMultiplier: null,
  ipoChargeFloorInCents: null,
  ipoLosOperator: "GT",
  ipoLosDays: null,
  ipoLosDaysSource: "MANUAL",
  ipoCodeChargeFloorInCents: 0,
  ipoOnlyCodesWithoutFeeSchedule: true,
  ipoDischargeStatusCodes: [...DEFAULT_TRANSFER_DISCHARGE_CODES],
  ipoFormulaType: "PERCENT_OF_EXCESS_CHARGES",
  ipoPerDiemSource: "MANUAL",
  ipoManualPerDiemInCents: null,
  ipoLosAdjustment: "NONE",
  ipoCapAtDrgRate: false,
  ipoExcessLosBase: null,
  ipoExcessLosBaseSource: "MANUAL",
  ipoFlatRateInCents: null,
  ipoPercent: null,
  ipoCaseRateInCents: null,
  ipoIncludedDays: null,
  ipoCaseRatePerDiemInCents: null,
  ipoMode: "ADDITIVE",
  contractLanguage: "",
};

interface IInpatientRulesetFormProps {
  title: string;
  submitLabel: string;
  submitIcon: string;
  formData: IInpatientRulesetFormData;
  onChange: (data: IInpatientRulesetFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** Rule id — present in Edit mode, enables attachments. */
  ruleId?: string;
  attachments?: IRuleAttachment[];
}

export function InpatientRulesetForm({
  title,
  submitLabel,
  submitIcon,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  ruleId,
  attachments,
}: IInpatientRulesetFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    if (!payers?.data) return [];
    return payers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [payers]);

  const handleInputChange = (
    field: keyof IInpatientRulesetFormData,
    value: any
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const handleTypeChange = (newType: InpatientRulesetType) => {
    onChange({
      ...formData,
      type: newType,
      // Reset type-specific fields when switching
      costToChargeRatio:
        newType === "FIXED_COST" ? formData.costToChargeRatio : null,
      fixedLossThresholdInCents:
        newType === "FIXED_COST" ? formData.fixedLossThresholdInCents : null,
      outlierPercent: newType === "FIXED_COST" ? formData.outlierPercent : 80,
      thresholdInCents:
        newType === "PASS_THROUGH" ? formData.thresholdInCents : 0,
      chargeMultiplier:
        newType === "PASS_THROUGH" ? formData.chargeMultiplier : null,
      formula:
        newType === "EXCESSIVE_LOS"
          ? formData.formula || "BCBST Day Outlier"
          : "BCBST Day Outlier",
      losFormula: newType === "TRANSFER" ? formData.losFormula : "LOS_PLUS_1",
      dischargeStatusCodes:
        newType === "TRANSFER"
          ? formData.dischargeStatusCodes
          : [...DEFAULT_TRANSFER_DISCHARGE_CODES],
      thresholdMultiplier:
        newType === "STOP_LOSS" ? formData.thresholdMultiplier : null,
      paymentPercent: newType === "STOP_LOSS" ? formData.paymentPercent : 75,
      chargeOutlierThresholdType:
        newType === "CHARGE_OUTLIER"
          ? formData.chargeOutlierThresholdType
          : "FLAT",
      chargeOutlierThresholdAmountInCents:
        newType === "CHARGE_OUTLIER"
          ? formData.chargeOutlierThresholdAmountInCents
          : null,
      chargeOutlierThresholdMultiplier:
        newType === "CHARGE_OUTLIER"
          ? formData.chargeOutlierThresholdMultiplier
          : null,
      chargeOutlierPercent:
        newType === "CHARGE_OUTLIER" ? formData.chargeOutlierPercent : null,
      chargeOutlierFloorInCents:
        newType === "CHARGE_OUTLIER"
          ? formData.chargeOutlierFloorInCents
          : null,
      // Reset INPATIENT_OUTLIER (CLE-2120) fields when switching away from this type
      ipoTriggerType:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoTriggerType
          : "STANDARD_CHARGE_THRESHOLD",
      ipoCostToChargeRatio:
        newType === "INPATIENT_OUTLIER" ? formData.ipoCostToChargeRatio : null,
      ipoThresholdMethod:
        newType === "INPATIENT_OUTLIER" ? formData.ipoThresholdMethod : "FIXED",
      ipoBaseReference:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoBaseReference
          : "BASE_PAYMENT",
      ipoFixedAmountInCents:
        newType === "INPATIENT_OUTLIER" ? formData.ipoFixedAmountInCents : null,
      ipoMultiplier:
        newType === "INPATIENT_OUTLIER" ? formData.ipoMultiplier : null,
      ipoChargeFloorInCents:
        newType === "INPATIENT_OUTLIER" ? formData.ipoChargeFloorInCents : null,
      ipoLosOperator:
        newType === "INPATIENT_OUTLIER" ? formData.ipoLosOperator : "GT",
      ipoLosDays: newType === "INPATIENT_OUTLIER" ? formData.ipoLosDays : null,
      ipoLosDaysSource:
        newType === "INPATIENT_OUTLIER" ? formData.ipoLosDaysSource : "MANUAL",
      ipoCodeChargeFloorInCents:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoCodeChargeFloorInCents
          : 0,
      ipoOnlyCodesWithoutFeeSchedule:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoOnlyCodesWithoutFeeSchedule
          : true,
      ipoDischargeStatusCodes:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoDischargeStatusCodes
          : [...DEFAULT_TRANSFER_DISCHARGE_CODES],
      ipoFormulaType:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoFormulaType
          : "PERCENT_OF_EXCESS_CHARGES",
      ipoPerDiemSource:
        newType === "INPATIENT_OUTLIER" ? formData.ipoPerDiemSource : "MANUAL",
      ipoManualPerDiemInCents:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoManualPerDiemInCents
          : null,
      ipoLosAdjustment:
        newType === "INPATIENT_OUTLIER" ? formData.ipoLosAdjustment : "NONE",
      ipoCapAtDrgRate:
        newType === "INPATIENT_OUTLIER" ? formData.ipoCapAtDrgRate : false,
      ipoExcessLosBase:
        newType === "INPATIENT_OUTLIER" ? formData.ipoExcessLosBase : null,
      ipoExcessLosBaseSource:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoExcessLosBaseSource
          : "MANUAL",
      ipoFlatRateInCents:
        newType === "INPATIENT_OUTLIER" ? formData.ipoFlatRateInCents : null,
      ipoPercent: newType === "INPATIENT_OUTLIER" ? formData.ipoPercent : null,
      ipoCaseRateInCents:
        newType === "INPATIENT_OUTLIER" ? formData.ipoCaseRateInCents : null,
      ipoIncludedDays:
        newType === "INPATIENT_OUTLIER" ? formData.ipoIncludedDays : null,
      ipoCaseRatePerDiemInCents:
        newType === "INPATIENT_OUTLIER"
          ? formData.ipoCaseRatePerDiemInCents
          : null,
      ipoMode: newType === "INPATIENT_OUTLIER" ? formData.ipoMode : "ADDITIVE",
    });
  };

  const commonFieldsValid =
    (formData.isDefault || (formData.providerId && formData.payerId)) &&
    formData.name.trim() &&
    formData.drgCodes.length > 0;

  const toggleDefault = (checked: boolean) => {
    onChange({
      ...formData,
      isDefault: checked,
      providerId: checked ? null : formData.providerId,
      payerId: checked ? null : formData.payerId,
    });
  };

  let typeFieldsValid = false;
  if (formData.type === "FIXED_COST") {
    typeFieldsValid =
      formData.costToChargeRatio != null &&
      formData.fixedLossThresholdInCents != null &&
      formData.outlierPercent != null;
  } else if (formData.type === "PASS_THROUGH") {
    typeFieldsValid =
      formData.thresholdInCents != null && formData.chargeMultiplier != null;
  } else if (formData.type === "EXCESSIVE_LOS") {
    typeFieldsValid = !!formData.formula;
  } else if (formData.type === "TRANSFER") {
    typeFieldsValid =
      !!formData.losFormula && formData.dischargeStatusCodes.length > 0;
  } else if (formData.type === "STOP_LOSS") {
    typeFieldsValid =
      formData.thresholdMultiplier != null && formData.paymentPercent != null;
  } else if (formData.type === "CHARGE_OUTLIER") {
    typeFieldsValid =
      formData.chargeOutlierPercent != null &&
      (formData.chargeOutlierThresholdType === "FLAT"
        ? formData.chargeOutlierThresholdAmountInCents != null
        : formData.chargeOutlierThresholdMultiplier != null);
  } else if (formData.type === "INPATIENT_OUTLIER") {
    let triggerValid = false;
    if (
      formData.ipoTriggerType === "STANDARD_CHARGE_THRESHOLD" ||
      formData.ipoTriggerType === "MODIFIED_CHARGE_THRESHOLD"
    ) {
      const ccrOk =
        formData.ipoTriggerType !== "MODIFIED_CHARGE_THRESHOLD" ||
        formData.ipoCostToChargeRatio != null;
      if (formData.ipoThresholdMethod === "FIXED") {
        triggerValid = ccrOk && formData.ipoFixedAmountInCents != null;
      } else if (formData.ipoThresholdMethod === "MULTIPLIER") {
        triggerValid =
          ccrOk &&
          formData.ipoMultiplier != null &&
          !!formData.ipoBaseReference;
      } else if (formData.ipoThresholdMethod === "BASE_PLUS_FIXED") {
        triggerValid =
          ccrOk &&
          formData.ipoFixedAmountInCents != null &&
          !!formData.ipoBaseReference;
      }
    } else if (formData.ipoTriggerType === "LOS_RANGE") {
      triggerValid =
        formData.ipoLosDaysSource === "FEE_SCHEDULE" ||
        formData.ipoLosDays != null;
    } else if (formData.ipoTriggerType === "PER_CODE_CHARGE_THRESHOLD") {
      triggerValid =
        formData.ipoCodeChargeFloorInCents != null &&
        formData.ipoCodeChargeFloorInCents >= 0;
    } else if (formData.ipoTriggerType === "DISCHARGE_STATUS") {
      triggerValid = formData.ipoDischargeStatusCodes.length > 0;
    } else if (formData.ipoTriggerType === "ALWAYS") {
      triggerValid = true;
    }

    let formulaValid = false;
    if (formData.ipoFormulaType === "PER_DIEM_X_LOS") {
      formulaValid =
        formData.ipoPerDiemSource !== "MANUAL" ||
        formData.ipoManualPerDiemInCents != null;
    } else if (formData.ipoFormulaType === "PER_DIEM_X_EXCESS_LOS") {
      const perDiemOk =
        formData.ipoPerDiemSource !== "MANUAL" ||
        formData.ipoManualPerDiemInCents != null;
      // When trigger is LOS Range, excess base auto-couples to trigger Days at save time —
      // trigger validation already covers that the days value is set.
      const excessOk =
        formData.ipoTriggerType === "LOS_RANGE" ||
        formData.ipoExcessLosBaseSource === "FEE_SCHEDULE" ||
        formData.ipoExcessLosBase != null;
      formulaValid = perDiemOk && excessOk;
    } else if (formData.ipoFormulaType === "FLAT_RATE") {
      formulaValid = formData.ipoFlatRateInCents != null;
    } else if (
      formData.ipoFormulaType === "PERCENT_OF_CHARGES" ||
      formData.ipoFormulaType === "PERCENT_OF_EXCESS_CHARGES"
    ) {
      formulaValid = formData.ipoPercent != null;
    } else if (formData.ipoFormulaType === "CASE_RATE_PLUS_PER_DIEM") {
      formulaValid =
        formData.ipoCaseRateInCents != null &&
        formData.ipoIncludedDays != null &&
        formData.ipoIncludedDays >= 0 &&
        formData.ipoCaseRatePerDiemInCents != null;
    }

    typeFieldsValid = triggerValid && formulaValid && !!formData.ipoMode;
  }

  const isValid = commonFieldsValid && typeFieldsValid;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        <div
          className="flex align-items-center gap-2 p-3 mb-3"
          style={{ background: "#f3f4f6", borderRadius: 6 }}
        >
          <Checkbox
            inputId="isDefault"
            checked={!!formData.isDefault}
            onChange={(e) => toggleDefault(!!e.checked)}
          />
          <label
            htmlFor="isDefault"
            style={{ fontWeight: 600, cursor: "pointer" }}
          >
            Global default rule
          </label>
          <span style={{ color: "#6b7280", fontSize: 12, marginLeft: 8 }}>
            No provider or payer. Other contracts can copy this rule.
          </span>
        </div>
        <div className="grid">
          {!formData.isDefault && (
            <>
              <div className="col-12 md:col-6 field">
                <label htmlFor="provider">Provider *</label>
                <Dropdown
                  id="provider"
                  className="w-full"
                  value={formData.providerId}
                  options={providerOptions}
                  onChange={(e) => handleInputChange("providerId", e.value)}
                  placeholder="Select Provider"
                  filter
                />
              </div>
              <div className="col-12 md:col-6 field">
                <label htmlFor="payer">Payer *</label>
                <Dropdown
                  id="payer"
                  className="w-full"
                  value={formData.payerId}
                  options={payerOptions}
                  onChange={(e) => handleInputChange("payerId", e.value)}
                  placeholder="Select Payer"
                  filter
                />
              </div>
            </>
          )}
          <div className="col-12 md:col-6 field">
            <label htmlFor="name">Name *</label>
            <InputText
              id="name"
              className="w-full"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="e.g., BCBS DRG 807 Fixed Cost"
            />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="type">Type *</label>
            <Dropdown
              id="type"
              className="w-full"
              value={formData.type}
              options={RULESET_TYPES}
              onChange={(e) => handleTypeChange(e.value)}
            />
          </div>
          <div className="col-12 field">
            <label htmlFor="drgCodes">DRG Codes *</label>
            <Chips
              id="drgCodes"
              className="w-full"
              value={formData.drgCodes}
              onChange={(e) => handleInputChange("drgCodes", e.value || [])}
              placeholder="Type a DRG code and press Enter"
              separator=","
            />
            <small className="text-500">
              Press Enter or type comma to add each DRG code
            </small>
          </div>
        </div>
      </Card>

      {formData.type === "FIXED_COST" && (
        <Card title="Fixed Cost Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure high-cost outlier payment parameters for inpatient DRG
            cases. When a patient&apos;s estimated cost exceeds the fixed-loss
            threshold, an outlier payment is added to the expected amount.
          </div>
          <div className="grid">
            <div className="col-12 md:col-4 field">
              <label htmlFor="ccr">Cost-to-Charge Ratio (CCR) *</label>
              <InputNumber
                id="ccr"
                className="w-full"
                value={formData.costToChargeRatio}
                onValueChange={(e) =>
                  handleInputChange("costToChargeRatio", e.value)
                }
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={4}
                min={0}
                max={2}
                placeholder="e.g., 0.95"
              />
            </div>
            <div className="col-12 md:col-4 field">
              <label htmlFor="threshold">Fixed-Loss Threshold *</label>
              <InputNumber
                id="threshold"
                className="w-full"
                value={
                  formData.fixedLossThresholdInCents != null
                    ? formData.fixedLossThresholdInCents / 100
                    : undefined
                }
                onValueChange={(e) =>
                  handleInputChange(
                    "fixedLossThresholdInCents",
                    e.value != null ? Math.round(e.value * 100) : null
                  )
                }
                mode="currency"
                currency="USD"
                locale="en-US"
                min={0}
                placeholder="e.g., $200.00"
              />
            </div>
            <div className="col-12 md:col-4 field">
              <label htmlFor="outlierPct">Outlier Percentage *</label>
              <InputNumber
                id="outlierPct"
                className="w-full"
                value={formData.outlierPercent}
                onValueChange={(e) =>
                  handleInputChange("outlierPercent", e.value)
                }
                suffix="%"
                min={0}
                max={100}
                placeholder="e.g., 80"
              />
            </div>
          </div>
          <InpatientCalculationExample type="FIXED_COST" values={formData} />
        </Card>
      )}

      {formData.type === "PASS_THROUGH" && (
        <Card title="Pass Through Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure pass-through calculation for inpatient claims with no fee
            schedule match. When total charges exceed the threshold, the
            expected amount is calculated as charges multiplied by the charge
            multiplier.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="ptThreshold">Charge Threshold *</label>
              <InputNumber
                id="ptThreshold"
                className="w-full"
                value={
                  formData.thresholdInCents != null
                    ? formData.thresholdInCents / 100
                    : undefined
                }
                onValueChange={(e) =>
                  handleInputChange(
                    "thresholdInCents",
                    e.value != null ? Math.round(e.value * 100) : null
                  )
                }
                mode="currency"
                currency="USD"
                locale="en-US"
                min={0}
                placeholder="e.g., $0.00"
              />
              <small className="text-500">
                Claims with charges above this threshold will use pass-through
                pricing
              </small>
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="chargeMultiplier">Charge Multiplier *</label>
              <InputNumber
                id="chargeMultiplier"
                className="w-full"
                value={formData.chargeMultiplier}
                onValueChange={(e) =>
                  handleInputChange("chargeMultiplier", e.value)
                }
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={4}
                min={0.01}
                max={10}
                placeholder="e.g., 1.25"
              />
              <small className="text-500">
                Expected amount = Total Charges x Multiplier
              </small>
            </div>
          </div>
          <InpatientCalculationExample type="PASS_THROUGH" values={formData} />
        </Card>
      )}

      {formData.type === "EXCESSIVE_LOS" && (
        <Card title="Excessive LOS Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure day-outlier calculation for inpatient DRG cases with
            excessive length of stay. The formula params (ALOS/GLOS, threshold,
            multiplier) come from the ProviderProcedure DRG fields.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="formula">Formula *</label>
              <Dropdown
                id="formula"
                className="w-full"
                value={formData.formula}
                options={EXCESSIVE_LOS_FORMULAS}
                onChange={(e) => handleInputChange("formula", e.value)}
                placeholder="Select Formula"
              />
            </div>
          </div>
          <InpatientCalculationExample type="EXCESSIVE_LOS" values={formData} />
        </Card>
      )}

      {formData.type === "TRANSFER" && (
        <Card title="Transfer Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure transfer per-diem pricing for inpatient DRG claims. When a
            claim&apos;s discharge status matches the configured codes, the
            expected amount is calculated as Per Diem × LOS (or LOS - 1, or LOS
            + 1).
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="losFormula">LOS Formula *</label>
              <Dropdown
                id="losFormula"
                className="w-full"
                value={formData.losFormula}
                options={TRANSFER_LOS_FORMULAS}
                onChange={(e) => handleInputChange("losFormula", e.value)}
                placeholder="Select LOS Formula"
              />
            </div>
            <div className="col-12 field">
              <label htmlFor="dischargeStatusCodes">
                Discharge Status Codes *
              </label>
              <Chips
                id="dischargeStatusCodes"
                className="w-full"
                value={formData.dischargeStatusCodes}
                onChange={(e) =>
                  handleInputChange("dischargeStatusCodes", e.value || [])
                }
                placeholder="Type a status code and press Enter"
                separator=","
              />
              <small className="text-500">
                Claims with these discharge status codes will use transfer
                pricing
              </small>
            </div>
          </div>
          <InpatientCalculationExample type="TRANSFER" values={formData} />
        </Card>
      )}

      {formData.type === "STOP_LOSS" && (
        <Card title="Stop Loss Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure stop loss payment for inpatient DRG cases. When billed
            charges exceed the threshold (a multiple of the DRG payment), the
            expected payment is replaced with a percentage of billed charges.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="thresholdMultiplier">
                Threshold (% of DRG Payment) *
              </label>
              <InputNumber
                id="thresholdMultiplier"
                className="w-full"
                value={formData.thresholdMultiplier}
                onValueChange={(e) =>
                  handleInputChange("thresholdMultiplier", e.value)
                }
                suffix="%"
                min={1}
                max={1000}
                placeholder="e.g., 200"
              />
              <small className="text-500">
                Stop loss triggers when charges exceed this % of the DRG payment
              </small>
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="paymentPercent">Payment (% of Charges) *</label>
              <InputNumber
                id="paymentPercent"
                className="w-full"
                value={formData.paymentPercent}
                onValueChange={(e) =>
                  handleInputChange("paymentPercent", e.value)
                }
                suffix="%"
                min={1}
                max={100}
                placeholder="e.g., 75"
              />
              <small className="text-500">
                Expected payment = this % of total billed charges
              </small>
            </div>
          </div>
          <InpatientCalculationExample type="STOP_LOSS" values={formData} />
        </Card>
      )}

      {formData.type === "CHARGE_OUTLIER" && (
        <Card title="Charge Outlier Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure charge-based outlier payment for inpatient cases. When
            billed charges exceed the threshold, an outlier payment is
            calculated as a percentage of the excess charges and added to the
            base expected amount.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="chargeOutlierThresholdType">
                Threshold Type *
              </label>
              <Dropdown
                id="chargeOutlierThresholdType"
                className="w-full"
                value={formData.chargeOutlierThresholdType}
                options={CHARGE_OUTLIER_THRESHOLD_TYPES}
                onChange={(e) =>
                  handleInputChange("chargeOutlierThresholdType", e.value)
                }
              />
            </div>
            {formData.chargeOutlierThresholdType === "FLAT" && (
              <div className="col-12 md:col-6 field">
                <label htmlFor="chargeOutlierThresholdAmount">
                  Threshold Amount *
                </label>
                <InputNumber
                  id="chargeOutlierThresholdAmount"
                  className="w-full"
                  value={
                    formData.chargeOutlierThresholdAmountInCents != null
                      ? formData.chargeOutlierThresholdAmountInCents / 100
                      : undefined
                  }
                  onValueChange={(e) =>
                    handleInputChange(
                      "chargeOutlierThresholdAmountInCents",
                      e.value != null ? Math.round(e.value * 100) : null
                    )
                  }
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  min={0}
                  placeholder="e.g., $125,000.00"
                />
                <small className="text-500">
                  Outlier triggers when charges exceed this amount
                </small>
              </div>
            )}
            {formData.chargeOutlierThresholdType === "MULTIPLIER" && (
              <div className="col-12 md:col-6 field">
                <label htmlFor="chargeOutlierThresholdMultiplier">
                  Threshold Multiplier *
                </label>
                <InputNumber
                  id="chargeOutlierThresholdMultiplier"
                  className="w-full"
                  value={formData.chargeOutlierThresholdMultiplier}
                  onValueChange={(e) =>
                    handleInputChange(
                      "chargeOutlierThresholdMultiplier",
                      e.value
                    )
                  }
                  mode="decimal"
                  minFractionDigits={1}
                  maxFractionDigits={2}
                  min={0.1}
                  max={100}
                  placeholder="e.g., 3.0"
                />
                <small className="text-500">
                  Threshold = this multiplier &times; base expected amount
                </small>
              </div>
            )}
            <div className="col-12 md:col-6 field">
              <label htmlFor="chargeOutlierPercent">
                Outlier Percentage (% of Excess) *
              </label>
              <InputNumber
                id="chargeOutlierPercent"
                className="w-full"
                value={formData.chargeOutlierPercent}
                onValueChange={(e) =>
                  handleInputChange("chargeOutlierPercent", e.value)
                }
                suffix="%"
                min={1}
                max={100}
                placeholder="e.g., 30"
              />
              <small className="text-500">
                Outlier payment = this % of charges above the threshold
              </small>
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="chargeOutlierFloor">
                Charge Floor (Optional)
              </label>
              <InputNumber
                id="chargeOutlierFloor"
                className="w-full"
                value={
                  formData.chargeOutlierFloorInCents != null
                    ? formData.chargeOutlierFloorInCents / 100
                    : undefined
                }
                onValueChange={(e) =>
                  handleInputChange(
                    "chargeOutlierFloorInCents",
                    e.value != null ? Math.round(e.value * 100) : null
                  )
                }
                mode="currency"
                currency="USD"
                locale="en-US"
                min={0}
                placeholder="e.g., $100,000.00"
              />
              <small className="text-500">
                If set, charges must also exceed this minimum to trigger the
                outlier
              </small>
            </div>
          </div>
          <InpatientCalculationExample
            type="CHARGE_OUTLIER"
            values={formData}
          />
        </Card>
      )}

      {formData.type === "INPATIENT_OUTLIER" && (
        <>
          <Card title="Trigger" className="mb-4">
            <div className="text-600 mb-3">
              Pick when this rule fires. Charge thresholds compare claim charges
              (raw or modified by a cost-to-charge ratio) to a computed
              threshold. LOS range fires when length of stay matches the
              comparison.
            </div>
            <div className="grid">
              <div className="col-12 md:col-6 field">
                <label htmlFor="ipoTriggerType">Trigger Type *</label>
                <Dropdown
                  id="ipoTriggerType"
                  className="w-full"
                  value={formData.ipoTriggerType}
                  options={IPO_TRIGGER_TYPES}
                  onChange={(e) => handleInputChange("ipoTriggerType", e.value)}
                />
              </div>

              {formData.ipoTriggerType === "MODIFIED_CHARGE_THRESHOLD" && (
                <div className="col-12 md:col-6 field">
                  <label htmlFor="ipoCcr">Cost-to-Charge Ratio *</label>
                  <InputNumber
                    id="ipoCcr"
                    className="w-full"
                    value={formData.ipoCostToChargeRatio}
                    onValueChange={(e) =>
                      handleInputChange("ipoCostToChargeRatio", e.value)
                    }
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={4}
                    min={0}
                    max={2}
                    placeholder="e.g., 0.95"
                  />
                  <small className="text-500">
                    Charges are multiplied by this before comparison
                  </small>
                </div>
              )}

              {(formData.ipoTriggerType === "STANDARD_CHARGE_THRESHOLD" ||
                formData.ipoTriggerType === "MODIFIED_CHARGE_THRESHOLD") && (
                <>
                  <div className="col-12 md:col-6 field">
                    <label htmlFor="ipoThresholdMethod">
                      Threshold Method *
                    </label>
                    <Dropdown
                      id="ipoThresholdMethod"
                      className="w-full"
                      value={formData.ipoThresholdMethod}
                      options={IPO_THRESHOLD_METHODS}
                      onChange={(e) =>
                        handleInputChange("ipoThresholdMethod", e.value)
                      }
                    />
                  </div>

                  {(formData.ipoThresholdMethod === "MULTIPLIER" ||
                    formData.ipoThresholdMethod === "BASE_PLUS_FIXED") && (
                    <div className="col-12 md:col-6 field">
                      <label htmlFor="ipoBaseReference">Base Reference *</label>
                      <Dropdown
                        id="ipoBaseReference"
                        className="w-full"
                        value={formData.ipoBaseReference}
                        options={IPO_BASE_REFERENCES}
                        onChange={(e) =>
                          handleInputChange("ipoBaseReference", e.value)
                        }
                      />
                    </div>
                  )}

                  {(formData.ipoThresholdMethod === "FIXED" ||
                    formData.ipoThresholdMethod === "BASE_PLUS_FIXED") && (
                    <div className="col-12 md:col-6 field">
                      <label htmlFor="ipoFixedAmount">
                        {formData.ipoThresholdMethod === "FIXED"
                          ? "Threshold Amount *"
                          : "Amount Added to Base *"}
                      </label>
                      <InputNumber
                        id="ipoFixedAmount"
                        className="w-full"
                        value={
                          formData.ipoFixedAmountInCents != null
                            ? formData.ipoFixedAmountInCents / 100
                            : undefined
                        }
                        onValueChange={(e) =>
                          handleInputChange(
                            "ipoFixedAmountInCents",
                            e.value != null ? Math.round(e.value * 100) : null
                          )
                        }
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        min={0}
                      />
                    </div>
                  )}

                  {formData.ipoThresholdMethod === "MULTIPLIER" && (
                    <div className="col-12 md:col-6 field">
                      <label htmlFor="ipoMultiplier">Multiplier *</label>
                      <InputNumber
                        id="ipoMultiplier"
                        className="w-full"
                        value={formData.ipoMultiplier}
                        onValueChange={(e) =>
                          handleInputChange("ipoMultiplier", e.value)
                        }
                        mode="decimal"
                        minFractionDigits={1}
                        maxFractionDigits={2}
                        min={0.1}
                        max={100}
                        placeholder="e.g., 3.8"
                      />
                    </div>
                  )}

                  <div className="col-12 md:col-6 field">
                    <label htmlFor="ipoChargeFloor">
                      Charge Floor (Optional)
                    </label>
                    <InputNumber
                      id="ipoChargeFloor"
                      className="w-full"
                      value={
                        formData.ipoChargeFloorInCents != null
                          ? formData.ipoChargeFloorInCents / 100
                          : undefined
                      }
                      onValueChange={(e) =>
                        handleInputChange(
                          "ipoChargeFloorInCents",
                          e.value != null ? Math.round(e.value * 100) : null
                        )
                      }
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      min={0}
                    />
                    <small className="text-500">
                      If set, effective charges must also exceed this minimum
                    </small>
                  </div>
                </>
              )}

              {formData.ipoTriggerType === "LOS_RANGE" && (
                <>
                  <div className="col-12 md:col-4 field">
                    <label htmlFor="ipoLosOperator">Operator *</label>
                    <Dropdown
                      id="ipoLosOperator"
                      className="w-full"
                      value={formData.ipoLosOperator}
                      options={IPO_LOS_OPERATORS}
                      onChange={(e) =>
                        handleInputChange("ipoLosOperator", e.value)
                      }
                    />
                  </div>
                  <div className="col-12 md:col-4 field">
                    <label htmlFor="ipoLosDaysSource">Days Source *</label>
                    <Dropdown
                      id="ipoLosDaysSource"
                      className="w-full"
                      value={formData.ipoLosDaysSource}
                      options={IPO_DAYS_SOURCES}
                      onChange={(e) =>
                        handleInputChange("ipoLosDaysSource", e.value)
                      }
                    />
                  </div>
                  <div className="col-12 md:col-4 field">
                    <label htmlFor="ipoLosDays">
                      Days{" "}
                      {formData.ipoLosDaysSource === "MANUAL"
                        ? "*"
                        : "(read-only)"}
                    </label>
                    <InputNumber
                      id="ipoLosDays"
                      className="w-full"
                      value={formData.ipoLosDays}
                      onValueChange={(e) =>
                        handleInputChange("ipoLosDays", e.value)
                      }
                      min={0}
                      max={365}
                      disabled={formData.ipoLosDaysSource === "FEE_SCHEDULE"}
                      placeholder={
                        formData.ipoLosDaysSource === "FEE_SCHEDULE"
                          ? "From drgLosOutlierThreshold"
                          : "e.g., 2"
                      }
                    />
                  </div>
                </>
              )}

              {formData.ipoTriggerType === "PER_CODE_CHARGE_THRESHOLD" && (
                <>
                  <div className="col-12 field">
                    <small className="text-500">
                      Per-code rules use the DRG Codes field (above) to specify
                      which CPT codes the rule matches. The rule iterates over
                      service lines on each claim, fires per matching line, and
                      sums the results.
                    </small>
                  </div>
                  <div className="col-12 md:col-6 field">
                    <label htmlFor="ipoCodeChargeFloor">
                      Per-Line Charge Floor *
                    </label>
                    <InputNumber
                      id="ipoCodeChargeFloor"
                      className="w-full"
                      value={
                        formData.ipoCodeChargeFloorInCents != null
                          ? formData.ipoCodeChargeFloorInCents / 100
                          : undefined
                      }
                      onValueChange={(e) =>
                        handleInputChange(
                          "ipoCodeChargeFloorInCents",
                          e.value != null ? Math.round(e.value * 100) : null
                        )
                      }
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      min={0}
                      placeholder="$0.00"
                    />
                    <small className="text-500">
                      A line&apos;s charges must exceed this for the rule to
                      fire on that line. Set to $0 to fire on any matching line.
                    </small>
                  </div>
                  <div className="col-12 md:col-6 field flex align-items-center gap-2">
                    <Checkbox
                      inputId="ipoOnlyCodesWithoutFeeSchedule"
                      checked={formData.ipoOnlyCodesWithoutFeeSchedule}
                      onChange={(e) =>
                        handleInputChange(
                          "ipoOnlyCodesWithoutFeeSchedule",
                          !!e.checked
                        )
                      }
                    />
                    <label htmlFor="ipoOnlyCodesWithoutFeeSchedule">
                      Only apply to codes without a fee schedule match
                    </label>
                    <small className="text-500 ml-2">
                      Default ON — matches legacy Pass Through behavior
                    </small>
                  </div>
                </>
              )}

              {formData.ipoTriggerType === "DISCHARGE_STATUS" && (
                <div className="col-12 field">
                  <label htmlFor="ipoDischargeStatusCodes">
                    Discharge Status Codes *
                  </label>
                  <Chips
                    id="ipoDischargeStatusCodes"
                    className="w-full"
                    value={formData.ipoDischargeStatusCodes}
                    onChange={(e) =>
                      handleInputChange(
                        "ipoDischargeStatusCodes",
                        e.value || []
                      )
                    }
                    placeholder="Type a status code and press Enter"
                    separator=","
                  />
                  <small className="text-500">
                    Claims with these discharge status codes (from 837 CL1-03)
                    will fire this rule. Default codes match legacy Transfer
                    behavior.
                  </small>
                </div>
              )}

              {formData.ipoTriggerType === "ALWAYS" && (
                <div className="col-12 field">
                  <small className="text-500">
                    The rule fires on every claim that matches the DRG codes
                    above — no other conditions. Use this when the rule IS the
                    base payment method for those DRGs (e.g., OB case rate
                    replacing the fee schedule).
                  </small>
                </div>
              )}
            </div>
          </Card>

          <Card title="Formula" className="mb-4">
            <div className="text-600 mb-3">
              Pick how the payment amount is calculated when the trigger fires.
              Calculated per-diem options pull values from the fee schedule
              (ProviderProcedure) at runtime.
            </div>
            <div className="grid">
              <div className="col-12 md:col-6 field">
                <label htmlFor="ipoFormulaType">Formula *</label>
                <Dropdown
                  id="ipoFormulaType"
                  className="w-full"
                  value={formData.ipoFormulaType}
                  options={IPO_FORMULA_TYPES}
                  onChange={(e) => handleInputChange("ipoFormulaType", e.value)}
                />
              </div>

              {(formData.ipoFormulaType === "PER_DIEM_X_LOS" ||
                formData.ipoFormulaType === "PER_DIEM_X_EXCESS_LOS") &&
                formData.ipoTriggerType === "LOS_RANGE" &&
                formData.ipoLosDaysSource === "FEE_SCHEDULE" && (
                  <div className="col-12 field">
                    <small className="text-500">
                      Per Diem Source is auto-coupled to the trigger&apos;s Fee
                      Schedule source —{" "}
                      <strong>(DRG Rate ÷ ALOS) × Outlier Multiplier</strong>{" "}
                      from ProviderProcedure. To use a different per-diem
                      source, set the trigger&apos;s Days Source to Manual.
                    </small>
                  </div>
                )}

              {(formData.ipoFormulaType === "PER_DIEM_X_LOS" ||
                formData.ipoFormulaType === "PER_DIEM_X_EXCESS_LOS") &&
                !(
                  formData.ipoTriggerType === "LOS_RANGE" &&
                  formData.ipoLosDaysSource === "FEE_SCHEDULE"
                ) && (
                  <>
                    <div className="col-12 md:col-6 field">
                      <label htmlFor="ipoPerDiemSource">
                        Per Diem Source *
                      </label>
                      <Dropdown
                        id="ipoPerDiemSource"
                        className="w-full"
                        value={formData.ipoPerDiemSource}
                        options={IPO_PER_DIEM_SOURCES}
                        onChange={(e) =>
                          handleInputChange("ipoPerDiemSource", e.value)
                        }
                      />
                    </div>
                    {formData.ipoPerDiemSource === "MANUAL" && (
                      <div className="col-12 md:col-6 field">
                        <label htmlFor="ipoManualPerDiem">
                          Manual Per Diem *
                        </label>
                        <InputNumber
                          id="ipoManualPerDiem"
                          className="w-full"
                          value={
                            formData.ipoManualPerDiemInCents != null
                              ? formData.ipoManualPerDiemInCents / 100
                              : undefined
                          }
                          onValueChange={(e) =>
                            handleInputChange(
                              "ipoManualPerDiemInCents",
                              e.value != null ? Math.round(e.value * 100) : null
                            )
                          }
                          mode="currency"
                          currency="USD"
                          locale="en-US"
                          min={0}
                          placeholder="e.g., $4,827.00"
                        />
                      </div>
                    )}
                  </>
                )}

              {formData.ipoFormulaType === "PER_DIEM_X_EXCESS_LOS" &&
                formData.ipoTriggerType === "LOS_RANGE" && (
                  <div className="col-12 field">
                    <small className="text-500">
                      Excess LOS Base is auto-coupled to the trigger&apos;s
                      <strong> Days</strong> ({formData.ipoLosDays ?? "—"}) and{" "}
                      <strong>Days Source</strong> (
                      {formData.ipoLosDaysSource === "MANUAL"
                        ? "Manual"
                        : "Fee Schedule"}
                      ). Adjust those above to change them.
                    </small>
                  </div>
                )}

              {formData.ipoFormulaType === "PER_DIEM_X_EXCESS_LOS" &&
                formData.ipoTriggerType !== "LOS_RANGE" && (
                  <>
                    <div className="col-12 md:col-4 field">
                      <label htmlFor="ipoExcessLosBaseSource">
                        Excess LOS Base Source *
                      </label>
                      <Dropdown
                        id="ipoExcessLosBaseSource"
                        className="w-full"
                        value={formData.ipoExcessLosBaseSource}
                        options={IPO_DAYS_SOURCES}
                        onChange={(e) =>
                          handleInputChange("ipoExcessLosBaseSource", e.value)
                        }
                      />
                    </div>
                    <div className="col-12 md:col-4 field">
                      <label htmlFor="ipoExcessLosBase">
                        Excess LOS Base{" "}
                        {formData.ipoExcessLosBaseSource === "MANUAL"
                          ? "*"
                          : "(read-only)"}
                      </label>
                      <InputNumber
                        id="ipoExcessLosBase"
                        className="w-full"
                        value={formData.ipoExcessLosBase}
                        onValueChange={(e) =>
                          handleInputChange("ipoExcessLosBase", e.value)
                        }
                        min={0}
                        max={365}
                        disabled={
                          formData.ipoExcessLosBaseSource === "FEE_SCHEDULE"
                        }
                        placeholder={
                          formData.ipoExcessLosBaseSource === "FEE_SCHEDULE"
                            ? "From drgLosOutlierThreshold"
                            : "e.g., 2"
                        }
                      />
                    </div>
                  </>
                )}

              {formData.ipoFormulaType === "FLAT_RATE" && (
                <div className="col-12 md:col-6 field">
                  <label htmlFor="ipoFlatRate">Flat Rate *</label>
                  <InputNumber
                    id="ipoFlatRate"
                    className="w-full"
                    value={
                      formData.ipoFlatRateInCents != null
                        ? formData.ipoFlatRateInCents / 100
                        : undefined
                    }
                    onValueChange={(e) =>
                      handleInputChange(
                        "ipoFlatRateInCents",
                        e.value != null ? Math.round(e.value * 100) : null
                      )
                    }
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    min={0}
                    placeholder="e.g., $4,350.00"
                  />
                </div>
              )}

              {(formData.ipoFormulaType === "PERCENT_OF_CHARGES" ||
                formData.ipoFormulaType === "PERCENT_OF_EXCESS_CHARGES") && (
                <div className="col-12 md:col-6 field">
                  <label htmlFor="ipoPercent">Percent *</label>
                  <InputNumber
                    id="ipoPercent"
                    className="w-full"
                    value={formData.ipoPercent}
                    onValueChange={(e) =>
                      handleInputChange("ipoPercent", e.value)
                    }
                    suffix="%"
                    min={0}
                    max={100}
                    placeholder={
                      formData.ipoFormulaType === "PERCENT_OF_CHARGES"
                        ? "e.g., 70"
                        : "e.g., 30"
                    }
                  />
                </div>
              )}

              {formData.ipoFormulaType === "CASE_RATE_PLUS_PER_DIEM" && (
                <>
                  <div className="col-12 field">
                    <small className="text-500">
                      Pays the case rate covering the first N included days,
                      then the per diem for every day beyond that. Example:
                      $5,796 + ($1,591/day × max(0, LOS − 2)).
                    </small>
                  </div>
                  <div className="col-12 md:col-4 field">
                    <label htmlFor="ipoCaseRate">Case Rate *</label>
                    <InputNumber
                      id="ipoCaseRate"
                      className="w-full"
                      value={
                        formData.ipoCaseRateInCents != null
                          ? formData.ipoCaseRateInCents / 100
                          : undefined
                      }
                      onValueChange={(e) =>
                        handleInputChange(
                          "ipoCaseRateInCents",
                          e.value != null ? Math.round(e.value * 100) : null
                        )
                      }
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      min={0}
                      placeholder="e.g., $5,796.00"
                    />
                  </div>
                  <div className="col-12 md:col-4 field">
                    <label htmlFor="ipoIncludedDays">Included Days *</label>
                    <InputNumber
                      id="ipoIncludedDays"
                      className="w-full"
                      value={formData.ipoIncludedDays}
                      onValueChange={(e) =>
                        handleInputChange("ipoIncludedDays", e.value)
                      }
                      min={0}
                      max={365}
                      placeholder="e.g., 2"
                    />
                    <small className="text-500">
                      Days covered by the case rate (per diem applies after)
                    </small>
                  </div>
                  <div className="col-12 md:col-4 field">
                    <label htmlFor="ipoCaseRatePerDiem">
                      Per Diem (after included days) *
                    </label>
                    <InputNumber
                      id="ipoCaseRatePerDiem"
                      className="w-full"
                      value={
                        formData.ipoCaseRatePerDiemInCents != null
                          ? formData.ipoCaseRatePerDiemInCents / 100
                          : undefined
                      }
                      onValueChange={(e) =>
                        handleInputChange(
                          "ipoCaseRatePerDiemInCents",
                          e.value != null ? Math.round(e.value * 100) : null
                        )
                      }
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      min={0}
                      placeholder="e.g., $1,591.00"
                    />
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card title="Mode" className="mb-4">
            <div className="text-600 mb-3">
              Pick how the formula result enters the final expected amount
              resolution. Replace overrides everything (including transfer
              pricing). Add stacks with the base 837 expected. Max competes via
              Math.max() with the other expected amounts.
            </div>
            <div className="grid">
              <div className="col-12 md:col-6 field">
                <label htmlFor="ipoMode">Mode *</label>
                <Dropdown
                  id="ipoMode"
                  className="w-full"
                  value={formData.ipoMode}
                  options={IPO_MODES}
                  onChange={(e) => handleInputChange("ipoMode", e.value)}
                />
              </div>
            </div>
            <InpatientCalculationExample
              type="INPATIENT_OUTLIER"
              values={formData}
            />
          </Card>
        </>
      )}

      <ContractLanguageSection
        ruleType="INPATIENT"
        ruleId={ruleId}
        attachments={attachments}
        value={formData.contractLanguage}
        onChange={(v) => handleInputChange("contractLanguage", v)}
      />

      <div className="flex justify-content-end gap-2">
        <Button
          label="Cancel"
          className="p-button-outlined"
          onClick={() => navigate(`/payer-rules`)}
        />
        <Button
          label={submitLabel}
          icon={submitIcon}
          disabled={!isValid || isSubmitting}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
